/**
 * The bottom sheet the ticket becomes below 1120px (components.md §1).
 *
 * Between 768 and 1119px it is triggered by the UP/DOWN buttons; below 768px it
 * is the whole order-entry surface, opened from the sticky bottom bar. Same
 * component, different trigger.
 *
 * It is a sheet and not a modal for a reason the layout table implies: the chart
 * and the countdown have to stay visible behind it. A user placing an order on a
 * phone is watching a clock that has fourteen seconds left, and a scrim that
 * hides it would make the sheet the most hostile surface in the product. So the
 * scrim is there for dismissal, the sheet takes the bottom of the viewport, and
 * `maxHeight` leaves the strip and the countdown uncovered.
 *
 * `env(safe-area-inset-bottom)` is respected because the sticky bottom bar sits
 * on the home indicator on every recent iPhone, and `viewport-fit=cover` is
 * already set in `index.html`.
 */

import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  className?: string
}

export function Sheet({ open, onClose, title, children, className }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    panel.current?.focus()
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col justify-end"
      style={{ background: 'var(--ud-color-scrim)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cx(
          'flex max-h-[70vh] flex-col gap-3 rounded-t-xl border-t border-border-default bg-bg-surface p-4 outline-none',
          'motion-safe:animate-[ud-sheet-in_180ms_var(--ud-ease-out)_both]',
          className,
        )}
        style={{ paddingBottom: 'calc(var(--ud-space-4) + env(safe-area-inset-bottom))' }}
      >
        {/* A grab handle, not a control: the sheet is dismissed by the scrim,
            Escape or its own actions. It is here because a sheet with no handle
            reads as a page that has broken. */}
        <span
          aria-hidden="true"
          className="mx-auto h-0_5 w-8 shrink-0 rounded-full bg-border-strong"
        />
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-title-sm text-text-primary">{title}</h2>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="inline-flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] shrink-0 md:min-h-0 md:min-w-0 items-center justify-center rounded-sm px-1 text-body-lg text-text-tertiary hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
          >
            ×
          </button>
        </div>
        <div className="ud-scroll min-h-0 flex-1">{children}</div>
      </div>
    </div>
  )
}
